import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useGetSingleNewsQuery, useGetNewsPageFieldsQuery, useIncrementNewsViewCountMutation } from '../NewsPage/api/NewsApiApiSlice';
import SingleNewsCard from '../NewsPage/components/SingleNewsCard/SingleNewsCard';
import AIpopup from '../../components/AIPopup/AIpopup';
import NotFound from '../../components/NotFound/NotFound';
import Loader from '../../components/Loader/Loader'

import { ReactComponent as AdminIcon } from '../../assets/images/adminIcon.svg';
import { ReactComponent as NewsAiIcon } from '../../assets/images/newsAiIcon.svg';

import styles from './styles/NewsSinglePage.module.scss';
// React quill text editor styles
import '../../assets/styles/quill.core.css';
import '../../assets/styles/quill.snow.css';

function NewsSinglePage() {
  const { newsSlug } = useParams();
  const { data, isLoading, isFetching, isError, error } = useGetSingleNewsQuery(newsSlug);
  const { data: pageData } = useGetNewsPageFieldsQuery();
  const [incrementNewsViewCount] = useIncrementNewsViewCountMutation();

  //  Local state for AI popup
  const [showAIpopup, setShowAIpopup] = useState(false);

  useEffect(() => {
    if (data) {
      incrementNewsViewCount({ newsId: data.id });
    }
  }, [data, incrementNewsViewCount]);

  if (isLoading || isFetching) return <Loader />;
  if (isError || !data) return <NotFound />;

  const publishDate = new Date(data.publish_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  function getPostedTimeDuration(publishDate) {
    const now = new Date();
    const publish = new Date(publishDate);
    const diffInMs = now - publish;

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30); // Approximation
    const diffInYears = Math.floor(diffInDays / 365); // Approximation

    if (diffInYears > 0) {
      return `Posted ${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
    } else if (diffInMonths > 0) {
      return `Posted ${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    } else if (diffInWeeks > 0) {
      return `Posted ${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    } else if (diffInDays > 0) {
      return `Posted ${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `Posted ${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes > 0) {
      return `Posted ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }
    return 'Posted recently';
  }

  return (
    <div
      className={styles.articleContainer}
      style={{
        "--primary-color": pageData?.colors?.primary,
        "--secondary-color": pageData?.colors?.secondary,
        "--teritiary-color": pageData?.colors?.tertiary,
        "--shadow-color": pageData?.colors?.shadow,
        "--line-color": pageData?.colors?.line,
        "--ai-button-text": pageData?.colors?.aiButtonTextColor,
        "--ai-color": pageData?.colors?.aiColor,
        "--insights-title" : pageData?.colors?.insightsTitle,
        "--highlight-gradient-1": pageData?.colors?.highlightBlock.gradient1,
        "--highlight-gradient-2": pageData?.colors?.highlightBlock.gradient2,
        "--highlight-border": pageData?.colors?.highlightBlock.border,
        "--icon-gradient-1": pageData?.colors.aiIconColors[0],
        "--icon-gradient-2": pageData?.colors.aiIconColors[1],
        "--icon-gradient-3": pageData?.colors.aiIconColors[2],
        "--icon-gradient-4": pageData?.colors.aiIconColors[3] || "#D61663",
      }}
    >
      <div className="container">
        <div className={styles.insightsTitle}>
          <span>INSIGHTS/BLOG</span>
        </div>
        <h1 className={styles.articleTitle}>{data.title}</h1>

        {/* --- Meta Info + AI Button --- */}
        <div className={styles.articleMeta}>
          <div className={styles.adminDetails}>
            <div className={styles.iconWrapper}>
              <AdminIcon />
            </div>
            <div className={styles.metaInfo}>
              <span className={styles.AuthorName}>{data.author}</span>
              <span className={styles.Dot}>|</span>
              <span className={styles.Date}>{publishDate}</span>
              <span className={styles.Dot}>•</span>
              <span className={styles.PostedTime}>{getPostedTimeDuration(data.publish_date)}</span>
            </div>
          </div>
          {
            data.ai_summary && Object.keys(data.ai_summary).length > 0 && (
              <button
                className={styles.aiBtn}
                onClick={() => setShowAIpopup(true)} // open popup
              >
                <NewsAiIcon />
                Ai summary
              </button>
            )}
        </div>

        {/* --- Featured Image --- */}
        {data.featured_image && (
          <div className={styles.articleImage}>
            <img
              src={data.featured_image.file}
              alt={data.featured_image.alternative_text}
            />
          </div>
        )}

        {/* --- Content --- */}
        <div
          className={`${styles.articleContent} ql-editor`}
          dangerouslySetInnerHTML={{ __html: data.content }}
        ></div>

        {/* --- Related Posts --- */}
        {data.related_news && data.related_news.length > 0 && (
          <div className={styles.relatedNews}>
            <h2>Related Posts</h2>
            <div className={styles.listingWrapper}>
              {data.related_news.map((newsItem) => (
                <div key={newsItem.id} className={styles.cardWrapper}>
                  <SingleNewsCard
                    news={newsItem}
                    colors={pageData.colors}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Render AIpopup only when active */}
      {showAIpopup &&
        <AIpopup
          onClose={() => setShowAIpopup(false)}
          setPopupStatus={setShowAIpopup}
          data={data.ai_summary}
          newsTitle={data.title}
        />
      }
    </div>
  );
}

export default NewsSinglePage;
